import { useState } from 'react';
import {
  JOURNEY_STATES222,
  isEncouragingState222,
  journeyNextAction222,
  journeyStateLabels222,
  retentionSummary222,
  seasonLabels222,
  type ExperimentVariant222,
  type JourneyState222,
  type RetentionMetrics222,
} from '../domain/engagement222';
import { AppFrame, Card, Metric, PrimaryButton, SecondaryButton, StatusBanner } from '../components/ui';
import { GuideCharacter } from '../reactions/GuideCharacter';

const demoMetrics: Record<JourneyState222, RetentionMetrics222> = {
  newcomer: { visitCount: 1, firstExplorationCompleted: false, day1Return: false, day7Return: false, day30Return: false },
  firstExplorationPending: { visitCount: 2, firstExplorationCompleted: false, day1Return: true, day7Return: true, day30Return: true },
  firstWeekSettling: { visitCount: 4, firstExplorationCompleted: true, day1Return: true, day7Return: true, day30Return: true },
  steady: { visitCount: 22, firstExplorationCompleted: true, day1Return: true, day7Return: true, day30Return: true },
  nearGoal: { visitCount: 18, firstExplorationCompleted: true, day1Return: true, day7Return: true, day30Return: true },
  dormant: { visitCount: 9, firstExplorationCompleted: true, day1Return: true, day7Return: true, day30Return: false },
  returning: { visitCount: 11, firstExplorationCompleted: true, day1Return: true, day7Return: true, day30Return: true },
  familyLinkPending: { visitCount: 6, firstExplorationCompleted: true, day1Return: true, day7Return: true, day30Return: true },
  monthlyFinisher: { visitCount: 27, firstExplorationCompleted: true, day1Return: true, day7Return: true, day30Return: true },
};

const demoVariants: ExperimentVariant222[] = [
  { key: 'control', cardOrder: 'today,village,collection', copyKey: 'home.default' },
  { key: 'village_first', cardOrder: 'village,today,collection', copyKey: 'home.village_first' },
];

const demoThrottle = [
  { label: '캠페인 쿨다운', detail: '같은 알림은 지정 일수 안에 다시 가지 않아요.' },
  { label: '캠페인 총량', detail: '한 사용자에게 보낼 수 있는 횟수가 정해져 있어요.' },
  { label: '하루 상한', detail: '여러 캠페인이 겹쳐도 하루 2건을 넘지 않아요.' },
];

export function EngagementDemoScreen222() {
  const [stateIndex, setStateIndex] = useState(0);
  const [variantIndex, setVariantIndex] = useState(0);
  const state = JOURNEY_STATES222[stateIndex];
  const metrics = demoMetrics[state];
  const action = journeyNextAction222[state];
  const variant = demoVariants[variantIndex];

  return <AppFrame title="MRM 2.22 인게이지먼트 데모" backTo="/demo" screenClass="engagement-screen-2-22">
    <div className="demo-sample-banner">체험용 고정 여정 데이터 · 실제 계정과 완전 분리</div>
    <StatusBanner />

    <section className="engagement-hero-2-22">
      <GuideCharacter guide="snows" pose="guide" state="guide" alt="여정 상태를 안내하는 스노스" priority/>
      <div>
        <p className="eyebrow">Engagement Orchestrator</p>
        <h1>{journeyStateLabels222[state]}</h1>
        <p>사용자를 점수로 평가하지 않고 지금의 여정 상태로 구분합니다. 상태마다 다음 행동은 하나만 제안해요.</p>
      </div>
    </section>

    <div className="engagement-state-grid-2-22" aria-label="여정 상태">
      {JOURNEY_STATES222.map((item, index) => (
        <button type="button" key={item} className={index === stateIndex ? 'active' : ''} onClick={() => setStateIndex(index)}>
          {journeyStateLabels222[item]}
        </button>
      ))}
    </div>

    <Card>
      <div className="card-heading"><div><span>다음 행동</span><h2>제안은 하나뿐입니다</h2></div><strong>{seasonLabels222.winter} 시즌</strong></div>
      <p className={isEncouragingState222(state) ? 'engagement-welcome-2-22' : 'engagement-next-2-22'}>{action.copy}</p>
      <p className="engagement-note-2-22">이동 화면 <b>{action.to}</b></p>
      <div className="engagement-metric-row-2-22">
        <Metric label="방문" value={`${metrics.visitCount}일`}/>
        <Metric label="첫 탐험" value={metrics.firstExplorationCompleted ? '완료' : '미완료'}/>
        <Metric label="재방문" value={retentionSummary222(metrics)}/>
      </div>
      <p className="engagement-note-2-22">
        하루 쉬어도 벽돌·퍼즐·이글루는 그대로예요. 미접속은 실패로 기록되지 않습니다.
      </p>
    </Card>

    <Card>
      <div className="card-heading"><div><span>알림 제한</span><h2>같은 알림이 반복되지 않아요</h2></div><strong>3중</strong></div>
      <div className="engagement-throttle-list-2-22">
        {demoThrottle.map((item) => <div key={item.label}><b>{item.label}</b><span>{item.detail}</span></div>)}
      </div>
      <p className="engagement-note-2-22">알림 문구는 콘텐츠 코드가 아니라 캠페인 데이터에 있어 배포 없이 바꿉니다.</p>
    </Card>

    <Card>
      <div className="card-heading"><div><span>실험</span><h2>보상은 실험군과 무관합니다</h2></div><strong>{variant.key}</strong></div>
      <div className="engagement-variant-tabs-2-22">
        {demoVariants.map((item, index) => (
          <button type="button" key={item.key} className={index === variantIndex ? 'active' : ''} onClick={() => setVariantIndex(index)}>{item.key}</button>
        ))}
      </div>
      <p className="engagement-note-2-22">홈 카드 순서 <b>{variant.cardOrder}</b></p>
      <div className="engagement-metric-row-2-22">
        <Metric label="포인트" value="23P"/>
        <Metric label="얼음 벽돌" value="1개"/>
      </div>
      <p className="engagement-note-2-22">
        실험 변형은 표현만 담습니다. 보상 수량 필드를 아예 가질 수 없어서 실험군에 따라 달라질 수 없습니다.
      </p>
    </Card>

    <div className="demo-213-actions">
      <SecondaryButton onClick={() => { setStateIndex(0); setVariantIndex(0); }}>초기화</SecondaryButton>
      <PrimaryButton onClick={() => setStateIndex((stateIndex + 1) % JOURNEY_STATES222.length)}>다음 상태</PrimaryButton>
    </div>
  </AppFrame>;
}
